import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from '../../../api/gen/api/organization.service';
import { OrganizationDto } from '../../../api/gen/model/organizationDto';
import { EditOrganizationRequest } from '../../../api/gen/model/editOrganizationRequest';
import {
    JjamesButtonComponent, JJamesInputComponent, JJamesCardComponent,
    JJamesAlertComponent, COUNTRIES, Role
} from 'shared-lib';
import { RoleService } from '../../auth/role-service';

@Component({
    selector: 'app-org-edit',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        JjamesButtonComponent,
        JJamesInputComponent,
        JJamesCardComponent,
        JJamesAlertComponent
    ],
    templateUrl: './org-edit.component.html',
    styleUrls: ['./org-edit.component.scss']
})
export class OrgEditComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly orgService = inject(OrganizationService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly roleService = inject(RoleService);

    /** ISO 3166-1 alpha-2 options for the billing country dropdown. */
    readonly countries = COUNTRIES;

    orgForm!: FormGroup;
    orgId: string | null = null;
    /** Renaming an organization is reserved for administrators; the backend refuses it for anyone else. */
    canRenameOrganization = signal(false);
    loading = false;
    isSubmitting = false;
    errorMessage: string | null = null;
    successMessage: string | null = null;
    private currentParkingSpaceIds: string[] = [];
    private currentParkingZoneIds: string[] = [];

    ngOnInit(): void {
        this.orgId = this.route.snapshot.paramMap.get('orgId');
        this.orgForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
            description: [''],
            firstAddressLine: [''],
            secondAddressLine: [''],
            locality: [''],
            postCode: [''],
            region: [''],
            countryCode: ['']
        });

        // Disabling the control rather than hiding the field keeps the name visible - it is the
        // organization's identity - while the backend has the last word either way.
        Promise.all([
            this.roleService.checkAccess(Role.Administrator),
            this.roleService.checkAccess(Role.OperatorAdmin)
        ]).then(([isAdmin, isOperatorAdmin]) => {
            const canRename = isAdmin || isOperatorAdmin;
            this.canRenameOrganization.set(canRename);
            if (!canRename) {
                this.orgForm.get('name')?.disable();
            }
        });

        if (this.orgId) {
            this.loadOrganization();
        }
    }

    loadOrganization(): void {
        if (!this.orgId) return;
        this.loading = true;

        this.orgService.organizationsOrganizationIdFetchPost(this.orgId, {
            includeDeleted: false,
            includeValidity: true
        }).subscribe({
            next: (org: OrganizationDto) => {
                this.currentParkingSpaceIds = (org.parkingSpaces ?? [])
                    .map(s => s.parkingSpaceId).filter((id): id is string => !!id);
                this.currentParkingZoneIds = (org.parkingZones ?? [])
                    .map(z => z.parkingZoneId).filter((id): id is string => !!id);
                this.orgForm.patchValue({
                    name: org.name ?? '',
                    description: org.description ?? '',
                    firstAddressLine: org.billingAddress?.firstAddressLine ?? '',
                    secondAddressLine: org.billingAddress?.secondAddressLine ?? '',
                    locality: org.billingAddress?.locality ?? '',
                    postCode: org.billingAddress?.postCode ?? '',
                    region: org.billingAddress?.region ?? '',
                    countryCode: (org.billingAddress?.countryCode ?? '').toUpperCase()
                });
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading organization:', error);
                this.errorMessage = 'Failed to load organization details';
                this.loading = false;
            }
        });
    }

    onSubmit(): void {
        if (this.orgForm.valid && !this.isSubmitting && this.orgId) {
            this.isSubmitting = true;
            this.errorMessage = null;

            // getRawValue, not value: the name control is disabled for non-administrators and a
            // disabled control drops out of value, but the API requires the field. Sending the
            // loaded name back unchanged is not a rename, so it passes the backend's guard.
            const formVal = this.orgForm.getRawValue();
            const request: EditOrganizationRequest = {
                name: formVal.name,
                description: formVal.description || undefined,
                billingAddress: {
                    firstAddressLine: formVal.firstAddressLine || undefined,
                    secondAddressLine: formVal.secondAddressLine || undefined,
                    locality: formVal.locality || undefined,
                    postCode: formVal.postCode || undefined,
                    region: formVal.region || undefined,
                    countryCode: formVal.countryCode || undefined
                },
                billingConfiguration: {},
                parkingSpaceIds: this.currentParkingSpaceIds.length > 0 ? this.currentParkingSpaceIds : undefined,
                parkingZoneIds: this.currentParkingZoneIds.length > 0 ? this.currentParkingZoneIds : undefined
            };

            this.orgService.organizationsOrganizationIdPut(this.orgId, request).subscribe({
                next: () => {
                    this.isSubmitting = false;
                    this.successMessage = 'Organization updated successfully!';
                    setTimeout(() => {
                        this.router.navigate(['/org-management', this.orgId]);
                    }, 1500);
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.errorMessage = error.error?.title || 'An error occurred while updating the organization.';
                    console.error('Error updating organization:', error);
                }
            });
        } else {
            this.markFormGroupTouched(this.orgForm);
        }
    }

    onCancel(): void {
        if (this.orgId) {
            this.router.navigate(['/org-management', this.orgId]);
        } else {
            this.router.navigate(['/org-management']);
        }
    }

    onClearError(): void {
        this.errorMessage = null;
    }

    onClearSuccess(): void {
        this.successMessage = null;
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.orgForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.orgForm.get(fieldName);
        if (field?.errors?.['required']) return `${fieldName} is required`;
        if (field?.errors?.['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters`;
        if (field?.errors?.['maxlength']) return `Maximum ${field.errors['maxlength'].requiredLength} characters`;
        return '';
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach(control => {
            control.markAsTouched();
        });
    }
}
